/* Candidate account-artwork picker. All identity and asset access are verified
 * by the same-origin app proxy; selection never implies a setup-fee waiver. */
(() => {
  'use strict';
  function mount({container, client, onSelect}) {
    if (!container) return null;
    const document = container.ownerDocument;
    container.className = 'wcf-saved-logo-picker';
    container.innerHTML = `<button type="button" data-library-open aria-expanded="false">Choose a saved logo</button>
      <p class="wcf-preview-note" data-library-status role="status"></p>
      <div data-library-panel hidden><p class="wcf-preview-note">Choose artwork from your account. Your order quote confirms any setup fee.</p>
        <div data-library-list class="wcf-saved-logo-list"></div>
        <div data-library-preview class="wcf-saved-logo-preview" hidden>
          <button type="button" data-use-thumbnail aria-label="Use this saved logo"><img data-library-image alt="Saved logo preview"></button>
          <span data-library-label></span><button type="button" data-library-use>Use this logo</button>
        </div>
      </div>`;
    const query = selector => container.querySelector(selector);
    const button = query('[data-library-open]'), panel = query('[data-library-panel]'), status = query('[data-library-status]');
    let loaded = false, selectedId = null, preview = null, previewUrl = null, generation = 0;
    const message = text => { status.textContent = text; };
    const label = asset => {
      const date = new Date(asset.created);
      const savedAt = Number.isFinite(date.getTime()) ? date.toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: 'numeric'}) : 'Date unavailable';
      return `${savedAt} · ${asset.id.slice(0, 8)}`;
    };
    function setSelected(id) {
      selectedId = id || null;
      container.querySelectorAll('[data-asset-id]').forEach(row => row.classList.toggle('is-selected', row.dataset.assetId === selectedId));
    }
    async function showPreview(asset, previewButton) {
      const current = ++generation;
      previewButton.disabled = true; message('Loading saved logo…');
      try {
        const blob = preview?.id === asset.id ? preview.blob : await client.assetBlob(asset.id);
        if (current !== generation || !container.isConnected) return;
        if (preview?.blob !== blob) {
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          previewUrl = URL.createObjectURL(blob);
        }
        preview = {id: asset.id, blob, label: label(asset)};
        query('[data-library-image]').src = previewUrl;
        query('[data-library-label]').textContent = preview.label;
        query('[data-library-preview]').hidden = false;
        message(selectedId === asset.id ? 'This saved logo is selected.' : 'Preview loaded. Choose “Use this logo” to select it.');
      } catch (error) { if (current === generation && container.isConnected) message(error.message || 'The logo could not be loaded.'); }
      finally { if (previewButton.isConnected) previewButton.disabled = false; }
    }
    function usePreview() {
      if (!preview || !container.isConnected) return;
      setSelected(preview.id);
      onSelect({assetId: preview.id, assetBlob: preview.blob, assetLabel: preview.label});
      message('Saved logo selected. Choose your application method separately.');
    }
    query('[data-library-use]').addEventListener('click', usePreview);
    query('[data-use-thumbnail]').addEventListener('click', usePreview);
    button.disabled = !client?.enabled;
    if (!client?.enabled) message('Saved logos are unavailable in this preview.');
    button.addEventListener('click', async () => {
      if (!client?.enabled) return;
      if (!panel.hidden) { panel.hidden = true; button.setAttribute('aria-expanded', 'false'); return; }
      panel.hidden = false; button.setAttribute('aria-expanded', 'true');
      if (loaded) return;
      button.disabled = true; message('Loading your saved logos…');
      try {
        const library = await client.library();
        if (!container.isConnected) return;
        if (!Array.isArray(library.assets)) throw new Error('Your saved logos could not be loaded.');
        const list = query('[data-library-list]');
        list.replaceChildren();
        for (const asset of library.assets) {
          if (!/^[0-9a-f-]{36}$/.test(asset?.id)) continue;
          const row = document.createElement('div'); row.className = 'wcf-saved-logo-row'; row.dataset.assetId = asset.id;
          const name = document.createElement('span'); name.textContent = label(asset);
          const previewButton = document.createElement('button'); previewButton.type = 'button'; previewButton.textContent = 'Preview';
          previewButton.setAttribute('aria-label', `Preview saved logo ${name.textContent}`);
          previewButton.addEventListener('click', () => showPreview(asset, previewButton));
          row.append(name, previewButton); list.append(row);
        }
        setSelected(selectedId); loaded = true;
        message(list.children.length ? 'Preview a logo to select it.' : 'No saved logos yet. You can upload a new logo.');
      } catch (error) {
        if (!container.isConnected) return;
        message(error.message === 'login required' ? 'Sign in to choose a saved logo, or upload a new logo.' : error.message || 'Your saved logos could not be loaded.');
        if (error.message === 'login required' && !query('[data-library-login]')) {
          const link = document.createElement('a'); link.dataset.libraryLogin = ''; link.href = '/account/login'; link.textContent = 'Sign in';
          status.append(' ', link);
        }
      } finally { if (button.isConnected) button.disabled = false; }
    });
    return Object.freeze({setSelected, destroy() {
      generation++;
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      previewUrl = null; preview = null;
    }});
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {mount};
  if (typeof window !== 'undefined') window.wcfSavedLogoPicker = Object.freeze({mount});
})();
